import * as yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Get user's timezone
const getUserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return "UTC";
    }
};

const userTimezone = getUserTimezone();

export const createCouponValidationSchema = yup.object({
    title: yup.string().required("Title is required"),
    description: yup.string().optional(),
    codes: yup.array().when("isCustomCode", {
        is: true,
        then: (schema) =>
            schema
                .of(yup.string().min(3, "Each code must be at least 3 characters long").required("Code is required"))
                .min(1, "At least one code is required"),
        otherwise: (schema) => schema.strip(),
    }),
    amountType: yup.string().oneOf(["FIXED", "PERCENTAGE"]).required("Amount type is required"),
    amountValue: yup
        .number()
        .transform((value) => (isNaN(value) || value === null || value === "" ? undefined : value))
        .required("Amount value is required")
        .min(0, "Amount value must be positive")
        .when("amountType", {
            is: "PERCENTAGE",
            then: (schema) => schema.max(100, "Percentage cannot exceed 100%"),
        }),
    startDate: yup.string().optional(),
    expireDate: yup
        .string()
        .optional()
        .test("expire-after-start", "Expire date must be after start date", function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;

            // Parse dates with user's timezone
            const startDateTime = dayjs(startDate).tz(userTimezone);
            const expireDateTime = dayjs(value).tz(userTimezone);

            return expireDateTime.isAfter(startDateTime);
        }),
    appliedTo: yup.string().oneOf(["ALL", "FIRST_SUBSCRIPTION"]).required("Applied to is required"),
    usagePerUser: yup
        .number()
        .transform((value) => (isNaN(value) || value === null || value === "" ? undefined : value))
        .when("showUsagePerUser", {
            is: true,
            then: (schema) => schema.required("Usage per user is required").min(1, "Usage per user must be at least 1"),
            otherwise: (schema) => schema.optional(),
        }),
    usageLimit: yup
        .number()
        .transform((value) => (isNaN(value) || value === null || value === "" ? undefined : value))
        .when("showUsageLimit", {
            is: true,
            then: (schema) => schema.required("Usage limit is required").min(1, "Usage limit must be at least 1"),
            otherwise: (schema) => schema.optional(),
        }),
    minimumPrice: yup
        .number()
        .transform((value) => (isNaN(value) || value === null || value === "" ? undefined : value))
        .when("showMinimumPrice", {
            is: true,
            then: (schema) => schema.required("Minimum price is required").min(0, "Minimum price must be positive"),
            otherwise: (schema) => schema.optional(),
        }),
    plans: yup.array().optional(),
    active: yup.boolean().required("Active status is required"),
    isCustomCode: yup.boolean().optional(),
    showStartDate: yup.boolean().optional(),
    showUsagePerUser: yup.boolean().optional(),
    showUsageLimit: yup.boolean().optional(),
    showMinimumPrice: yup.boolean().optional(),
});
